"use client";

import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import React, { useState, useRef, useEffect } from "react";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import ImageSkeleton from "./ImageSkeleton";

export function GalleryContent() {
  const categories = [
    { label: "Shot II", value: "Shot II" },
    { label: "Arpoador", value: "Arpoador" },
    { label: "Click Runner", value: "Click Runner" },
    { label: "Click com você", value: "Click com você" },
    { label: "Click nas ruas", value: "Click nas ruas" },
    { label: "Click no esporte", value: "Click no esporte" },
    { label: "Click sob rodas", value: "Click sob rodas" },
    { label: "Click no carnaval", value: "Click no carnaval" },
  ];

  const [isStart, setIsStart] = useState(true);
  const [isEnd, setIsEnd] = useState(false);
  const [selectedGallery, setSelectedGallery] = useState<string>("Shot II");
  const [selectedCategory, setSelectedCategory] = useState("Iron Man");
  const sliderRef = useRef<HTMLDivElement>(null);
  const categoryRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const handlePrev = () => {
    const currentIndex = categories.findIndex(
      (cat) => cat.value === selectedGallery
    );
    if (currentIndex > 0) {
      setSelectedGallery(categories[currentIndex - 1].value);
    }
  };

  const handleNext = () => {
    const currentIndex = categories.findIndex(
      (cat) => cat.value === selectedGallery
    );
    if (currentIndex < categories.length - 1) {
      setSelectedGallery(categories[currentIndex + 1].value);
    }
  };

  useEffect(() => {
    const currentIndex = categories.findIndex(
      (cat) => cat.value === selectedGallery
    );
    setIsStart(currentIndex === 0);
    setIsEnd(currentIndex === categories.length - 1);
  }, [selectedGallery]);

  useEffect(() => {
    const currentIndex = categories.findIndex(
      (cat) => cat.value === selectedGallery
    );

    if (currentIndex !== -1 && categoryRefs.current[currentIndex]) {
      categoryRefs.current[currentIndex]?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }
  }, [selectedGallery]);

  const getImages = (category: string) => {
    const counts: { [key: string]: number } = {
      "Shot II": 27,
      Arpoador: 26,
      "Click Runner": 26,
      "Click com você": 12,
      "Click nas ruas": 11,
      "Click no esporte": selectedCategory === "Iron Man" ? 11 : 8,
      "Click sob rodas": 12,
      "Click no carnaval": 14,
    };

    const basePath: { [key: string]: string } = {
      "Shot II": "/image/galeria/shot/",
      Arpoador: "/image/galeria/arpoador/",
      "Click Runner": "/image/galeria/click-runner/",
      "Click com você": "/image/galeria/com-voce/",
      "Click nas ruas": "/image/galeria/nas-ruas/",
      "Click no esporte":
        selectedCategory === "Iron Man"
          ? "/image/galeria/iron-man/"
          : "/image/galeria/bro-tour/",
      "Click sob rodas": "/image/galeria/nas-rodas/",
      "Click no carnaval": "/image/galeria/carnaval/",
    };

    const count = counts[category];
    const path = basePath[category];

    const images = [];
    for (let i = 1; i <= count; i++) {
      images.push(`${path}${i}.webp`);
    }
    return images;
  };

  return (
    <>
      <div ref={sliderRef} className="flex w-full max-w-[80rem] flex-col">
        <motion.h1
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2, delay: 0.1 }}
          className="mb-8 whitespace-nowrap text-5xl md:text-8xl"
        >
          Galeria
        </motion.h1>

        <div className="scrollbar-hidden mb-8 flex w-full flex-nowrap gap-2 overflow-x-auto rounded-2xl border p-1 lg:max-w-max lg:flex-wrap lg:overflow-visible">
          {categories.map((category, index) => (
            <motion.button
              key={category.label}
              ref={(el) => {
                categoryRefs.current[index] = el;
              }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setSelectedGallery(category.value)}
              className={`flex-shrink-0 whitespace-nowrap rounded-xl px-4 py-2 ${
                selectedGallery === category.value
                  ? "bg-[#E7EDE7BF] text-black"
                  : "bg-white text-gray-500"
              }`}
            >
              {category.label}
            </motion.button>
          ))}
        </div>
      </div>

      {selectedGallery === "Click no esporte" ? (
        <div className="flex w-full max-w-[80rem] flex-col items-center justify-center gap-3">
          <div className="mb-3 flex max-w-max gap-2 self-start rounded-2xl border p-1">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setSelectedCategory("Iron Man")}
              className={`whitespace-nowrap rounded-xl px-4 py-2 ${
                selectedCategory === "Iron Man" ? "bg-[#E7EDE7BF]" : "bg-gray-0"
              }`}
            >
              Iron Man
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setSelectedCategory("Olas Bro Tour")}
              className={`whitespace-nowrap rounded-xl px-4 py-2 ${
                selectedCategory === "Olas Bro Tour"
                  ? "bg-[#E7EDE7BF]"
                  : "bg-gray-0"
              }`}
            >
              Olas Pro Tour
            </motion.button>
          </div>

          <div className="masonry sm:masonry-sm md:masonry-md">
            {getImages(selectedGallery).map((src, imgIndex) => (
              <ImageSkeleton
                key={src}
                src={src}
                alt={`${selectedGallery} ${imgIndex + 1}`}
                className="mb-6 h-auto max-w-full"
              />
            ))}
          </div>
        </div>
      ) : (
        <div className="masonry sm:masonry-sm md:masonry-md">
          {getImages(selectedGallery).map((src, imgIndex) => (
            <ImageSkeleton
              key={src}
              src={src}
              alt={`${selectedGallery} ${imgIndex + 1}`}
              className="mb-6 h-auto max-w-full"
            />
          ))}
        </div>
      )}
      <div className="flex w-full items-center justify-center gap-3 py-10">
        <button
          type="button"
          className={cn(
            "flex h-8 w-8 items-center justify-center rounded-full bg-[#E0E0E3]",
            isStart && "cursor-not-allowed opacity-50"
          )}
          onClick={handlePrev}
          disabled={isStart}
        >
          <FaChevronLeft color="#626263" />
        </button>

        <button
          type="button"
          className={cn(
            "flex h-8 w-8 items-center justify-center rounded-full bg-[#E0E0E3]",
            isEnd && "cursor-not-allowed opacity-50"
          )}
          onClick={handleNext}
          disabled={isEnd}
        >
          <FaChevronRight color="#626263" />
        </button>
      </div>
    </>
  );
}
